import { Button } from '@odekoteam/doppio';
import { FORM_ERROR } from 'final-form';
import { Form } from 'react-final-form';
import { Flex, Heading } from 'theme-ui';

import { ErrorLabel } from '@/components/Collection/Error';
import Modal from '@/components/Modal';
import { useDeleteMasterHomepageMutation } from '@/graphql/types/generated';
import { useAppDispatch } from '@/hooks/store';
import { showToast, ToastAlertType } from '@/state/toast';

export function ModalDeleteHomepage({
  onClose,
  visible,
  onDeleted,
  masterHomepage,
}: {
  visible: boolean;
  masterHomepage?: {
    id: string;
    name: string;
  } | null;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const dispatch = useAppDispatch();
  const [, executeDelete] = useDeleteMasterHomepageMutation();

  const onSubmit = async () => {
    if (!masterHomepage) {
      return { [FORM_ERROR]: 'No master homepage selected' };
    }
    const result = await executeDelete({ input: { id: masterHomepage.id } });
    if (result.error) {
      return { [FORM_ERROR]: result.error.message };
    }
    const errors =
      result.data?.deleteMasterHomepage?.errors?.map(
        (error) => error.message,
      ) ?? [];
    if (errors.length > 0) {
      const message = errors.join(', ');
      return { [FORM_ERROR]: message };
    }
    dispatch(
      showToast({
        duration: 4000,
        alertType: ToastAlertType.SUCCESS,
        header: 'Master homepage deleted',
        content: 'The master homepage has been deleted.',
      }),
    );
    onDeleted();
    onClose();
    return;
  };

  return (
    <Modal visible={visible}>
      <Form
        onSubmit={onSubmit}
        render={({ submitting, submitError, handleSubmit }) => (
          <>
            <Heading variant="headingMd" sx={{ mb: 3 }}>
              Delete Homepage
            </Heading>
            <Flex sx={{ gap: 4, flexDirection: 'column' }}>
              <div>
                Are you sure you want to delete <b>{masterHomepage?.name}</b>?
                This will permanently remove all its rows and collection
                homepage assets. This action cannot be undone.
              </div>
              <div>
                (Deleting the homepage will not delete the Collection Pages. You
                can still find them in the Collection Page.)
              </div>
              <ErrorLabel message={submitError} />
              <Flex sx={{ gap: 3, justifyContent: 'flex-end' }}>
                <Button variant="ghost" onPress={onClose} disabled={submitting}>
                  Cancel
                </Button>
                <Button
                  variant="slay"
                  disabled={submitting}
                  onPress={handleSubmit}
                >
                  Delete
                </Button>
              </Flex>
            </Flex>
          </>
        )}
      />
    </Modal>
  );
}
